import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from '@/components/icons'
import { Button } from '@/components/ui/button'

interface State {
  error: Error | null
}

/**
 * Root-level error boundary — the page-of-last-resort. Route-level errors
 * should be handled inline inside the route (retry-in-place) so the shell
 * stays; this catches component-tree crashes we haven't foreseen.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[error-boundary]', error, info.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-md flex-col items-start justify-center gap-3 px-6">
        <div className="flex items-center gap-2 text-[color:var(--crit)]">
          <AlertTriangle className="size-5" />
          <h1 className="text-[15px] font-semibold">Something broke.</h1>
        </div>
        <p className="text-[12.5px] text-[color:var(--muted-foreground)]">
          Pulse hit an error it couldn&rsquo;t recover from. Try again — if the same page keeps
          failing, drop a note in{' '}
          <span className="font-medium text-[color:var(--foreground)]">#oms-ops</span> with the
          message below.
        </p>
        <pre className="max-w-full overflow-x-auto rounded-md bg-[color:var(--card)] p-2 shadow-[var(--shadow-raised)] text-[11.5px] text-[color:var(--muted-foreground)]">
          {this.state.error.message}
        </pre>
        <Button variant="outline" size="sm" onClick={this.reset}>
          <RefreshCw className="mr-1.5 size-3.5" /> Try again
        </Button>
      </div>
    )
  }
}
